import { Injectable } from '@angular/core'
import { Action, Selector, State, StateContext } from '@ngxs/store'
import { SchedulerStateModel } from './scheduler.model'
import { SchedulerService } from '../scheduler.service'
import {
  ApproveLocationSchedule,
  CancelRegionalManagerApproval,
  CopyPreviousLocationSchedule,
  CopySchedulerData,
  CopyScheduleTemplate,
  DeleteLocationSchedule,
  GetAllLocationSchedules,
  GetEmployeesBookedSchedule,
  GetLocationSchedule,
  GetPreviousLocationSchedule,
  GetScheduleAttendance,
  GetScheduleTemplate,
  RejectLocationSchedule,
  ResetPreviousLocationSchedule,
  SetLocationSchedule,
  SetSchedulerData,
  SetScheduleTemplate,
  SetScheduleTemplateData,
  SetScheduleTemplateOpenHours
} from './scheduler.actions'
import { cloneDeep, groupBy, sum, sumBy } from 'lodash'
import {
  IScheduledEmployee,
  ISchedulerEventInfo,
  ISchedulerEventTime
} from '../../interfaces/scheduler.interface'
import { IGenericObject } from '@lla-platform/core/core-data-access'
import { tap } from 'rxjs'
import { IScheduleTemplatePayload } from '../../interfaces/schedule-template.interface'
import {
  IScheduleAttendanceInterval,
  IScheduleAttendanceResponse,
  IScheduleAttendanceWeekDay
} from '../../interfaces/schedule-attendance.interface'

@State<SchedulerStateModel>({
  name: 'scheduler'
})
@Injectable()
export class SchedulerState {
  constructor(private schedulerService: SchedulerService) {}

  @Selector()
  static allLocationSchedules(state: SchedulerStateModel) {
    return state.allLocationSchedules
  }

  @Selector()
  static locationSchedule(state: SchedulerStateModel) {
    return state.locationSchedule
  }

  @Selector()
  static summary(state: SchedulerStateModel) {
    return state.summary
  }

  @Selector()
  static previousSummary(state: SchedulerStateModel) {
    return state.previousSummary
  }

  @Selector()
  static employeesBookedSchedule(state: SchedulerStateModel) {
    return state.employeesBookedSchedule
  }

  @Selector()
  static hasUnsavedChanges(state: SchedulerStateModel) {
    return state.hasUnsavedChanges
  }

  @Selector()
  static copySkippedEmployees(state: SchedulerStateModel) {
    return state.copySkippedEmployees
  }

  @Selector()
  static scheduleTemplate(state: SchedulerStateModel) {
    return state.scheduleTemplate
  }

  @Selector()
  static totalInfo(state: SchedulerStateModel) {
    const flatData = state.locationSchedule
      ?.map((el) => el.data.map((w) => w.employees))
      ?.flat()
      ?.flat()
      ?.filter((el) => !!el?.id)
      ?.map((el) => ({
        id: el?.id,
        totalTime: this.eventsToTimeRange(el?.events)
      }))
    if (!flatData) {
      return
    }
    const groupedData = groupBy(flatData, (x) => x?.id)
    const finalResult: IGenericObject<string> = {}
    Object.keys(groupedData).forEach((key) => {
      const element = groupedData[key]
      finalResult[key] = `${(sumBy(element, (x) => x.totalTime) ?? 0) / 60}h`
    })
    return finalResult
  }

  @Selector()
  static scheduleAttendance(state: SchedulerStateModel): IScheduleAttendanceResponse {
    if (!state.scheduleAttendance) {
      return { locations: [] }
    }

    return {
      locations: state.scheduleAttendance.map((location) => {
        const processedEmployees = location.employees.map((employee) => ({
          ...employee,
          workDays: employee.workDays.map((workDay) => ({
            ...workDay,
            attendanceIntervals: workDay.attendanceIntervals.map((interval) =>
              this.calculateIntervalPosition(interval)
            ),
            scheduleIntervals: workDay.scheduleIntervals.map((interval) =>
              this.calculateIntervalPosition(interval)
            )
          }))
        }))

        const weekDaysMap = new Map<string, IScheduleAttendanceWeekDay>()

        processedEmployees.forEach((employee) => {
          employee.workDays.forEach((workDay) => {
            const key = workDay.dayName

            if (!weekDaysMap.has(key)) {
              weekDaysMap.set(key, {
                date: workDay.date,
                dayName: workDay.dayName,
                weekDayEmployees: []
              })
            }

            const weekDay = weekDaysMap.get(key)
            if (weekDay) {
              weekDay.weekDayEmployees.push({
                employeeId: employee.employeeId,
                employeeName: employee.employeeName,
                hasPaidOvertime: employee.hasPaidOvertime,
                isArchived: employee.isArchived,
                workedHours: employee.workedHours,
                scheduledHours: employee.scheduledHours,
                workedHoursFormatted: workDay.workedHoursFormatted,
                scheduledHoursFormatted: workDay.scheduledHoursFormatted,
                attendanceIntervals: workDay.attendanceIntervals,
                scheduleIntervals: workDay.scheduleIntervals
              })
            }
          })
        })

        const weekDays = Array.from(weekDaysMap.values()).sort(
          (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()
        )

        return {
          ...location,
          employees: processedEmployees,
          weekDays
        }
      })
    }
  }

  @Action(SetSchedulerData)
  setSchedulerData(
    ctx: StateContext<SchedulerStateModel>,
    { workDayDate, templateIndex, employees }: SetSchedulerData
  ) {
    const locationSchedule = cloneDeep(ctx.getState()?.locationSchedule ?? [])
    const workDayIndex = locationSchedule?.findIndex((el) => el.workDayDate === workDayDate)
    if (workDayIndex < 0) {
      return
    }
    if (locationSchedule[workDayIndex] && locationSchedule[workDayIndex]?.data?.[templateIndex]) {
      locationSchedule[workDayIndex].data[templateIndex].employees = employees
    }
    ctx.patchState({
      locationSchedule,
      hasUnsavedChanges: true
    })
  }

  @Action(CopySchedulerData)
  copySchedulerData(ctx: StateContext<SchedulerStateModel>, { dayIndex }: CopySchedulerData) {
    const locationSchedule = cloneDeep(ctx.getState()?.locationSchedule ?? [])
    const dayInfo = locationSchedule[dayIndex]
    const previousDayTemplates = locationSchedule[dayIndex - 1].data
    const skipper = SchedulerState.archivedSkipper(ctx.getState())
    locationSchedule[dayIndex] = {
      ...dayInfo,
      data: dayInfo.data.map((el) => {
        const previousEmployees = previousDayTemplates.find(
          (p) => p.abbreviation === el.abbreviation
        )?.employees
        if (!previousEmployees) {
          return { ...el, employees: [...(el.employees ?? [])] }
        }
        return { ...el, employees: skipper.omit(previousEmployees) }
      })
    }
    ctx.patchState({
      locationSchedule,
      hasUnsavedChanges: true,
      copySkippedEmployees: skipper.skippedNames()
    })
  }

  @Action(GetLocationSchedule)
  getLocationSchedule(
    ctx: StateContext<SchedulerStateModel>,
    { locationId, weekNumber }: GetLocationSchedule
  ) {
    return this.schedulerService.getLocationSchedule(locationId, weekNumber).pipe(
      tap((res) => {
        ctx.patchState({
          locationSchedule: res?.locationWorkDaySchedules ?? [],
          summary: res?.summary,
          hasUnsavedChanges: false
        })
      })
    )
  }

  @Action(RejectLocationSchedule)
  rejectLocationSchedule(ctx: StateContext<SchedulerStateModel>, { payload }: RejectLocationSchedule) {
    return this.schedulerService.rejectLocationSchedule(payload).pipe(
      tap((res) => {
        ctx.patchState({
          summary: res
        })
      })
    )
  }

  @Action(ResetPreviousLocationSchedule)
  resetPreviousLocationSchedule(ctx: StateContext<SchedulerStateModel>) {
    ctx.patchState({
      previousLocationSchedule: undefined,
      previousSummary: undefined
    })
  }

  @Action(GetPreviousLocationSchedule)
  getPreviousLocationSchedule(
    ctx: StateContext<SchedulerStateModel>,
    { locationId, weekNumber }: GetPreviousLocationSchedule
  ) {
    return this.schedulerService.getLocationSchedule(locationId, weekNumber).pipe(
      tap((res) => {
        ctx.patchState({
          previousLocationSchedule: res?.locationWorkDaySchedules ?? [],
          previousSummary: res?.summary,
          hasUnsavedChanges: false
        })
      })
    )
  }

  @Action(CopyPreviousLocationSchedule)
  copyPreviousLocationSchedule(ctx: StateContext<SchedulerStateModel>) {
    const currentLocationSchedule = ctx.getState().locationSchedule
    const previousLocationSchedule = ctx.getState().previousLocationSchedule
    if (!previousLocationSchedule) {
      return
    }
    const skipper = SchedulerState.archivedSkipper(ctx.getState())
    const locationSchedule = previousLocationSchedule?.map((el) => ({
      ...el,
      workDayDate: currentLocationSchedule.find((p) => p.workDayName === el.workDayName)
        ?.workDayDate as Date,
      data: el.data.map((d) => ({
        ...d,
        employees: d.employees ? skipper.omit(d.employees) : d.employees
      }))
    }))

    ctx.patchState({
      locationSchedule,
      hasUnsavedChanges: true,
      copySkippedEmployees: skipper.skippedNames()
    })
  }

  @Action(GetEmployeesBookedSchedule)
  getEmployeesBookedSchedule(
    ctx: StateContext<SchedulerStateModel>,
    { weekNumber }: GetEmployeesBookedSchedule
  ) {
    return this.schedulerService.getEmployeesBookedSchedule(weekNumber).pipe(
      tap((res) => {
        ctx.patchState({
          employeesBookedSchedule: res.employeeBookedSchedules ?? []
        })
      })
    )
  }

  @Action(SetLocationSchedule)
  setLocationSchedule(
    ctx: StateContext<SchedulerStateModel>,
    { locationId, weekNumber }: SetLocationSchedule
  ) {
    const locationSchedule = ctx.getState().locationSchedule

    if (!locationSchedule) {
      return
    }

    const employees = locationSchedule.flatMap((template) =>
      template.data.flatMap((dayData) =>
        (dayData.employees || [])
          .filter((employee) => !!employee.id)
          .map((employee) => ({
            id: employee.id,
            abbreviation: dayData.abbreviation,
            workDayDate: template.workDayDate,
            events: employee.events
          }))
      )
    )

    return this.schedulerService
      .setLocationSchedule({
        locationId,
        weekNumber,
        employees
      })
      .pipe(
        tap((res) => {
          ctx.patchState({
            summary: {
              ...(ctx.getState().summary ?? {}),
              locationScheduleId: res?.locationScheduleId
            },
            hasUnsavedChanges: false
          })
        })
      )
  }

  @Action(ApproveLocationSchedule)
  approveLocationSchedule(ctx: StateContext<SchedulerStateModel>, { payload }: ApproveLocationSchedule) {
    return this.schedulerService.approveLocationSchedule(payload).pipe(
      tap((res) => {
        ctx.patchState({
          summary: res
        })
      })
    )
  }

  @Action(GetAllLocationSchedules)
  getAllLocationSchedules(
    ctx: StateContext<SchedulerStateModel>,
    { weekNumber }: GetAllLocationSchedules
  ) {
    return this.schedulerService.getAllLocationSchedules(weekNumber).pipe(
      tap((res) => {
        ctx.patchState({
          allLocationSchedules: res?.locationSchedules ?? []
        })
      })
    )
  }

  @Action(DeleteLocationSchedule)
  deleteLocationSchedule(
    ctx: StateContext<SchedulerStateModel>,
    { locationScheduleId }: DeleteLocationSchedule
  ) {
    return this.schedulerService.deleteLocationSchedule(locationScheduleId)
  }

  @Action(GetScheduleTemplate)
  getScheduleTemplate(ctx: StateContext<SchedulerStateModel>, { locationId }: GetScheduleTemplate) {
    return this.schedulerService.getScheduleTemplate(locationId).pipe(
      tap((res) => {
        ctx.patchState({ scheduleTemplate: res?.scheduleTemplate ?? [], hasUnsavedChanges: false })
      })
    )
  }

  @Action(SetScheduleTemplate)
  setScheduleTemplate(ctx: StateContext<SchedulerStateModel>, { locationId }: SetScheduleTemplate) {
    const payload: IScheduleTemplatePayload = {
      scheduleTemplate: ctx.getState()?.scheduleTemplate ?? []
    }
    return this.schedulerService.setScheduleTemplate(locationId, payload).pipe(
      tap(() => {
        ctx.patchState({ hasUnsavedChanges: false })
      })
    )
  }

  @Action(CopyScheduleTemplate)
  copyScheduleTemplate(ctx: StateContext<SchedulerStateModel>, { dayIndex }: CopyScheduleTemplate) {
    const scheduleTemplate = cloneDeep(ctx.getState()?.scheduleTemplate ?? [])
    const dayInfo = scheduleTemplate[dayIndex]
    const previousDayTemplates = scheduleTemplate[dayIndex - 1]
    scheduleTemplate[dayIndex] = {
      ...dayInfo,
      openHours: previousDayTemplates.openHours,
      data: [...(previousDayTemplates.data ?? [])]
    }
    ctx.patchState({
      scheduleTemplate,
      hasUnsavedChanges: true
    })
  }

  @Action(SetScheduleTemplateData)
  setScheduleTemplateData(
    ctx: StateContext<SchedulerStateModel>,
    { workDayDate, abbreviation, defaultEvents, expectedEmployeeCount }: SetScheduleTemplateData
  ) {
    const scheduleTemplate = cloneDeep(ctx.getState()?.scheduleTemplate ?? [])
    const workDayIndex = scheduleTemplate?.findIndex((el) => el.workDayDate === workDayDate)
    if (workDayIndex < 0) {
      return
    }
    const dataIndex = scheduleTemplate[workDayIndex].data.findIndex(
      (el) => el.abbreviation === abbreviation
    )
    if (dataIndex < 0) {
      return
    }
    scheduleTemplate[workDayIndex].data[dataIndex].defaultEvents = [...defaultEvents]
    scheduleTemplate[workDayIndex].data[dataIndex].expectedEmployeeCount = expectedEmployeeCount
    ctx.patchState({
      scheduleTemplate,
      hasUnsavedChanges: true
    })
  }

  @Action(SetScheduleTemplateOpenHours)
  setScheduleTemplateOpenHours(
    ctx: StateContext<SchedulerStateModel>,
    { workDayDate, openHours }: SetScheduleTemplateOpenHours
  ) {
    const scheduleTemplate = cloneDeep(ctx.getState()?.scheduleTemplate ?? [])
    const workDayIndex = scheduleTemplate?.findIndex((el) => el.workDayDate === workDayDate)
    if (workDayIndex < 0) {
      return
    }
    scheduleTemplate[workDayIndex].openHours = { ...openHours }
    ctx.patchState({
      scheduleTemplate,
      hasUnsavedChanges: true
    })
  }

  @Action(CancelRegionalManagerApproval)
  cancelRegionalManagerApproval(
    ctx: StateContext<SchedulerStateModel>,
    { locationScheduleId }: CancelRegionalManagerApproval
  ) {
    return this.schedulerService.cancelRegionalManagerApproval(locationScheduleId).pipe(
      tap((res) => {
        ctx.patchState({
          summary: res
        })
      })
    )
  }

  @Action(GetScheduleAttendance)
  getScheduleAttendance(ctx: StateContext<SchedulerStateModel>, { payload }: GetScheduleAttendance) {
    ctx.patchState({ scheduleAttendance: [] })
    return this.schedulerService.getScheduleAttendance(payload).pipe(
      tap((res) => {
        ctx.patchState({ scheduleAttendance: res?.locations ?? [] })
      })
    )
  }

  // Drops archived employees from copied assignments while collecting the skipped names,
  // so copy actions can both filter and report who was skipped. See the "Assignable"
  // glossary entry in GLOSSARY.md.
  private static archivedSkipper(state: SchedulerStateModel) {
    const archivedEmployees = new Map(
      (state.employeesBookedSchedule ?? [])
        .filter((el) => el.isArchived)
        .map((el) => [el.id, `${el.firstName} ${el.lastName}`])
    )
    const skipped = new Set<string>()
    return {
      omit: (employees: IScheduledEmployee[]): IScheduledEmployee[] =>
        employees.filter((employee) => {
          const archivedName = employee.id ? archivedEmployees.get(employee.id) : undefined
          if (!archivedName) {
            return true
          }
          skipped.add(archivedName)
          return false
        }),
      skippedNames: (): string[] => [...skipped]
    }
  }

  public static eventsToTimeRange(events?: ISchedulerEventInfo[]) {
    if (!events) {
      return 0
    }
    return sum(events?.map((el) => this.timeToMins(el.end) - this.timeToMins(el.start)))
  }

  public static timeToMins(time: ISchedulerEventTime) {
    return time.hour * 60 + time.minute
  }

  public static minsToTime(mins: number): ISchedulerEventTime {
    return {
      hour: Math.floor(mins / 60),
      minute: mins % 60
    }
  }

  public static timeToLabel(time: ISchedulerEventTime): string {
    const hour = time.hour % 12 || 12
    const minute = time.minute === 0 ? '00' : time.minute.toString().padStart(2, '0')
    const period = time.hour >= 12 ? 'PM' : 'AM'
    return `${hour}:${minute} ${period}`
  }

  public static timeToCompactLabel(time: ISchedulerEventTime): string {
    const hour = time.hour % 12 || 12
    const minute = time.minute === 0 ? '00' : time.minute.toString().padStart(2, '0')
    return `${hour}:${minute}`
  }

  public static eventInfoToTimePeriod(eventInfo: ISchedulerEventInfo) {
    return `${this.timeToLabel(eventInfo.start)} - ${this.timeToLabel(eventInfo.end)}`
  }

  public static eventInfoToCompactTimePeriod(eventInfo: ISchedulerEventInfo) {
    return `${this.timeToCompactLabel(eventInfo.start)}-${this.timeToCompactLabel(eventInfo.end)}`
  }

  public static minsToLabel(mins: number) {
    const time = this.minsToTime(mins)
    return this.timeToLabel(time)
  }

  public static calculateIntervalPosition(interval: ISchedulerEventInfo): IScheduleAttendanceInterval {
    const totalMinutesInDay = 24 * 60 // 1440 minutes in a day (00:00 to 23:59)

    // Convert start and end times to minutes from midnight
    const startMinutes = this.timeToMins(interval.start)
    const endMinutes = this.timeToMins(interval.end)

    // Calculate position as percentage of the day
    const left = (startMinutes / totalMinutesInDay) * 100
    const width = ((endMinutes - startMinutes) / totalMinutesInDay) * 100

    return {
      ...interval,
      left: Math.max(0, Math.min(100, left)), // Ensure left is between 0-100
      width: Math.max(0, Math.min(100 - left, width)) // Ensure width doesn't exceed remaining space
    }
  }
}
